import { useEffect, useState } from "react";
import { Navbar } from "@/components/Navbar";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { StatusPill } from "@/components/CookBookingsTable";
import { StripeConnectPanel } from "@/components/StripeConnectPanel";
import { IcalPanel } from "@/components/IcalPanel";
import { KitchenIcalImport } from "@/components/KitchenIcalImport";
import { HostOnboardingChecklist } from "@/components/HostOnboardingChecklist";

export default function OwnerDashboard() {
  const [kitchens, setKitchens] = useState([]);
  const [bookings, setBookings] = useState([]);

  const load = async () => {
    const [k, b] = await Promise.all([
      api.get("/owner/kitchens"),
      api.get("/bookings/mine"),
    ]);
    setKitchens(k.data);
    setBookings(b.data);
  };
  useEffect(() => { load(); }, []);

  const decide = async (id, decision) => {
    try {
      await api.post(`/bookings/${id}/decision`, { decision });
      toast.success(`Booking ${decision}d`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed");
    }
  };

  const cancel = async (id) => {
    if (!window.confirm("Cancel this booking? If paid, the cook will be refunded.")) return;
    try {
      const { data } = await api.post(`/bookings/${id}/cancel`, { reason: "" });
      toast.success(data.refunded ? "Cancelled & cook refunded" : "Cancelled");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Cancel failed");
    }
  };

  const revenue = bookings.filter(b => b.payment_status === "paid").reduce((s,b)=>s+b.subtotal,0);
  const pending = bookings.filter(b => b.status === "pending").length;

  return (
    <div className="min-h-screen">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 py-14">
        <div className="flex items-baseline justify-between flex-wrap gap-4">
          <div>
            <div className="tick-label">Host</div>
            <h1 className="font-display font-black text-5xl tracking-tighter mt-2">Your kitchens.</h1>
          </div>
          <Link data-testid="new-kitchen-btn" to="/list-your-kitchen" className="btn-signal">+ Add kitchen</Link>
        </div>

        <HostOnboardingChecklist kitchens={kitchens} bookings={bookings} />

        <div className="mt-8">
          <StripeConnectPanel />
        </div>

        <div className="mt-4">
          <IcalPanel />
        </div>

        <div className="mt-8 grid grid-cols-2 md:grid-cols-4 border hairline">
          <Stat label="Kitchens" value={kitchens.length} />
          <Stat label="Pending requests" value={pending} />
          <Stat label="Paid earnings" value={`$${revenue.toFixed(2)}`} />
          <Stat label="After 15% fee" value={`$${(revenue * 0.85).toFixed(2)}`} />
        </div>

        {/* Kitchens */}
        <div className="mt-14">
          <div className="tick-label mb-4">My kitchens</div>
          {kitchens.length === 0 ? (
            <div className="border hairline p-10 text-center text-neutral-400">
              No kitchens yet. <Link to="/list-your-kitchen" className="text-[#FF3B30]">List your first one</Link>.
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {kitchens.map(k => (
                <Link key={k.id} to={`/kitchen/${k.id}`} data-testid={`owner-kitchen-${k.id}`} className="k-card p-5 block">
                  <div className="font-display text-xl font-bold">{k.name}</div>
                  <div className="text-neutral-500 text-sm">{k.city}</div>
                  <div className="mt-3 font-mono">${k.hourly_rate}/hr · {k.booking_mode}</div>
                </Link>
              ))}
            </div>
          )}
          {kitchens.length > 0 && (
            <div className="mt-6 space-y-3">
              <div className="tick-label">Calendar imports</div>
              {kitchens.map(k => <KitchenIcalImport key={k.id} kitchenId={k.id} />)}
            </div>
          )}
        </div>

        {/* Bookings */}
        <div className="mt-14">
          <div className="tick-label mb-4">Booking requests</div>
          {bookings.length === 0 ? (
            <div className="border hairline p-10 text-center text-neutral-500">No requests yet.</div>
          ) : (
            <div className="border hairline overflow-x-auto">
              <table className="w-full">
                <thead className="border-b hairline">
                  <tr className="text-left tick-label">
                    <th className="p-4">Cook</th>
                    <th className="p-4">Kitchen</th>
                    <th className="p-4">When</th>
                    <th className="p-4">You get</th>
                    <th className="p-4">Status</th>
                    <th className="p-4">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map(b => (
                    <tr key={b.id} data-testid={`owner-booking-${b.id}`} className="border-b hairline">
                      <td className="p-4">
                        <div className="font-semibold">{b.cook_name}</div>
                        {b.cook_license_url && (
                          <a href={`/api/files/${b.cook_license_url}`} target="_blank" rel="noreferrer" className="tick-label text-[#FF3B30]">View license</a>
                        )}
                      </td>
                      <td className="p-4">{b.kitchen_name}</td>
                      <td className="p-4 font-mono text-sm">{new Date(b.start_time).toLocaleString()}<br/><span className="text-neutral-500">{b.hours}h</span></td>
                      <td className="p-4 font-mono">${(b.subtotal * 0.85).toFixed(2)}</td>
                      <td className="p-4"><StatusPill s={b.status} /></td>
                      <td className="p-4">
                        {b.status === "pending" && (
                          <div className="flex gap-2">
                            <button data-testid={`approve-${b.id}`} onClick={() => decide(b.id, "approve")} className="btn-signal">Approve</button>
                            <button data-testid={`reject-${b.id}`} onClick={() => decide(b.id, "reject")} className="btn-outline">Reject</button>
                          </div>
                        )}
                        {["approved", "paid"].includes(b.status) && (
                          <button data-testid={`owner-cancel-${b.id}`} onClick={() => cancel(b.id)} className="btn-outline">Cancel</button>
                        )}
                        <Link to={`/messages/${b.id}`} className="btn-outline inline-block mt-2">Messages</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="p-6 border-r hairline last:border-r-0 border-b md:border-b-0 hairline">
      <div className="tick-label">{label}</div>
      <div className="font-mono text-3xl font-bold mt-2">{value}</div>
    </div>
  );
}
